import apiClient from '../utils/apiClient';

/**
 * User Service - Handles all user-related API calls
 */
class UserService {
  /**
   * Get current user profile
   * @returns {Promise<Object>} User profile data
   */
  async getCurrentUser() {
    return apiClient.getJson('/api/users/me');
  }

  /**
   * Update current user profile
   * @param {Object} userData - User data to update
   * @returns {Promise<Object>} Updated user profile
   */
  async updateCurrentUser(userData) {
    return apiClient.putJson('/api/users/me', userData);
  }

  /**
   * Update user profile partially
   * @param {Object} userData - Partial user data to update
   * @returns {Promise<Object>} Updated user profile
   */
  async patchCurrentUser(userData) {
    return apiClient.patchJson('/api/users/me', userData);
  }

  /**
   * Delete current user account
   * @returns {Promise<void>}
   */
  async deleteCurrentUser() {
    await apiClient.delete('/api/users/me');
  }

  /**
   * Get user by ID (admin only)
   * @param {string} userId - User ID
   * @returns {Promise<Object>} User profile data
   */
  async getUserById(userId) {
    return apiClient.getJson(`/api/users/${userId}`);
  }

  /**
   * Get all users (admin only)
   * @param {Object} params - Query parameters (page, size, search, etc.)
   * @returns {Promise<Object>} Paginated users list
   */
  async getAllUsers(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/api/users?${queryString}` : '/api/users';
    return apiClient.getJson(endpoint);
  }

  /**
   * Update user by ID (admin only)
   * @param {string} userId - User ID
   * @param {Object} userData - User data to update
   * @returns {Promise<Object>} Updated user profile
   */
  async updateUserById(userId, userData) {
    return apiClient.putJson(`/api/users/${userId}`, userData);
  }

  /**
   * Delete user by ID (admin only)
   * @param {string} userId - User ID
   * @returns {Promise<void>}
   */
  async deleteUserById(userId) {
    await apiClient.delete(`/api/users/${userId}`);
  }

  /**
   * Update user status (admin only)
   * @param {string} userId - User ID
   * @param {string} status - New status (ACTIVE, INACTIVE, SUSPENDED, DELETED)
   * @returns {Promise<Object>} Updated user profile
   */
  async updateUserStatus(userId, status) {
    return apiClient.patchJson(`/api/users/${userId}/status`, { status });
  }

  /**
   * Get user activity/audit log (admin only)
   * @param {string} userId - User ID
   * @param {Object} params - Query parameters (page, size, from, to, etc.)
   * @returns {Promise<Object>} Paginated activity log
   */
  async getUserActivity(userId, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString 
      ? `/api/users/${userId}/activity?${queryString}` 
      : `/api/users/${userId}/activity`;
    return apiClient.getJson(endpoint);
  }

  /**
   * Upload user avatar
   * @param {File} file - Avatar image file
   * @returns {Promise<Object>} Updated user profile with new avatar URL
   */
  async uploadAvatar(file) {
    const formData = new FormData();
    formData.append('avatar', file);
    
    const response = await apiClient.request('/api/users/me/avatar', {
      method: 'POST',
      body: formData,
      headers: {
        // Don't set Content-Type, let the browser set it with boundary for FormData
      },
    });
    
    return response.json();
  }

  /**
   * Delete user avatar
   * @returns {Promise<Object>} Updated user profile without avatar
   */
  async deleteAvatar() {
    return apiClient.putJson('/api/users/me/avatar');
  }

  /**
   * Change user password
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   * @returns {Promise<void>}
   */
  async changePassword(currentPassword, newPassword) {
    await apiClient.post('/api/users/me/change-password', {
      currentPassword,
      newPassword,
    });
  }

  /**
   * Request password reset
   * @param {string} email - User email
   * @returns {Promise<void>}
   */
  async requestPasswordReset(email) {
    await apiClient.post('/api/users/reset-password', { email });
  }

  /**
   * Reset password with token
   * @param {string} token - Reset token
   * @param {string} newPassword - New password
   * @returns {Promise<void>}
   */
  async resetPassword(token, newPassword) {
    await apiClient.post('/api/users/reset-password/confirm', {
      token,
      newPassword,
    });
  }

  /**
   * Enable two-factor authentication
   * @returns {Promise<Object>} QR code and secret for 2FA setup
   */
  async enableTwoFactor() {
    return apiClient.postJson('/api/users/me/2fa/enable');
  }

  /**
   * Verify and confirm two-factor authentication setup
   * @param {string} code - 2FA code from authenticator app
   * @returns {Promise<Object>} Backup codes
   */
  async confirmTwoFactor(code) {
    return apiClient.postJson('/api/users/me/2fa/confirm', { code });
  }

  /**
   * Disable two-factor authentication
   * @param {string} code - 2FA code from authenticator app
   * @returns {Promise<void>}
   */
  async disableTwoFactor(code) {
    await apiClient.post('/api/users/me/2fa/disable', { code });
  }

  /**
   * Get user preferences/settings
   * @returns {Promise<Object>} User preferences
   */
  async getUserPreferences() {
    return apiClient.getJson('/api/users/me/preferences');
  }

  /**
   * Update user preferences/settings
   * @param {Object} preferences - User preferences to update
   * @returns {Promise<Object>} Updated preferences
   */
  async updateUserPreferences(preferences) {
    return apiClient.putJson('/api/users/me/preferences', preferences);
  }
}

// Create singleton instance
const userService = new UserService();

export default userService;
